// Enhanced initialization plugin for state management with improved synchronization
import { useState, useFetch, navigateTo, useRoute } from '#app';

// Define response type to handle type checking
interface ValidationResponse {
  success: boolean;
  error?: string;
  exists?: boolean;
  budgetId?: string;
  debugInfo?: any;
  params?: {
    userId: string;
    month: number;
    year: number;
  };
}

export default defineNuxtPlugin({
  name: 'init-state',
  enforce: 'pre', // Run before other plugins
  setup() {
    // Initialize or reset the budget state on app initialization
    const budgetState = useState('budget', () => ({
      details: null,
      isLoading: false,
      isSaving: false,
      currentStep: 0,
      exists: false,
      existingId: null,
      initialized: false,
      lastUpdated: Date.now(), // Track when the state was last updated
      lastValidationTime: 0 // Track last time we validated budget existence
    }));

    // Route change tracking for validation
    const lastRoute = useState<string | null>('lastRoute', () => null);
    const currentRoute = useState<string | null>('currentRoute', () => null);

    // Only log on client side to avoid hydration mismatch
    if (process.client) {
      // Removed debug logging;
    }
    
    // Enhanced validation function with more robust caching controls
    const validateBudgetExists = async (userId: string, month: number, year: number, forceRefresh = false) => {
      if (!userId || !month || !year) {
        return { exists: false, budgetId: null };
      }

      try {
        // Add cache-busting parameter if forced refresh
        const cacheBuster = forceRefresh ? `&_t=${Date.now()}` : '';
        
        // Use direct $fetch to bypass caching completely
        const response = await $fetch<ValidationResponse>(`/api/budget/validate?userId=${userId}&month=${month}&year=${year}${cacheBuster}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        // Only log on client side
        if (process.client) {
          // Removed debug logging;
        }
        
        if (!response.success) {
          if (process.client) {
            // Removed error logging;
          }
          return { exists: false, budgetId: null };
        }

        // Update last validation time
        budgetState.value.lastValidationTime = Date.now();

        return {
          exists: response.exists === true,
          budgetId: response.budgetId || null
        };
      } catch (error: any) {
        if (process.client) {
          // Removed error logging;
        }
        return { exists: false, budgetId: null };
      }
    };

    // Helper function to get next month
    const getNextMonth = () => {
      const today = new Date();
      const nextMonthDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      return {
        month: nextMonthDate.getMonth() + 1, // 1-based month
        year: nextMonthDate.getFullYear()
      };
    };
    
    // Enhanced budget state refresher with improved synchronization
    const refreshBudgetState = async (route: any, forceRefresh = false) => {
      let userId = useState<string>('selectedUser').value;
      
      // If userId is testid or invalid, try to get the real user ID
      if (!userId || userId === 'testid') {
        if (process.client) {
          // Removed debug logging;
        }
        try {
          // Try to get the authenticated user first
          const authResponse = await $fetch('/api/auth/user');
          if (authResponse?.success && authResponse.user && authResponse.user.id !== 'testid') {
            if (process.client) {
              // Removed debug logging;
            }
            userId = authResponse.user.id;
            useState('selectedUser').value = userId;
          } else {
            // Fall back to getting the first user
            const usersResponse = await $fetch('/api/users');
            if (usersResponse?.success && usersResponse.users?.length > 0) {
              if (process.client) {
                // Removed debug logging;
              }
              userId = usersResponse.users[0].id;
              useState('selectedUser').value = userId;
            }
          }
        } catch (error) {
          if (process.client) {
            // Removed error logging;
          }
        }
      }
      
      if (!userId || userId === 'testid') {
        if (process.client) {
          // Removed error logging;
        }
        return { exists: false, budgetId: null };
      }

      // Get route if not provided
      if (!route) {
        route = useRoute();
      }

      // Get current time
      const now = Date.now();
      
      // Skip refresh if we've validated recently (30 seconds) unless forced
      if (!forceRefresh && (now - budgetState.value.lastValidationTime) < 30000) {
        if (process.client) {
          // Removed debug logging;
        }
        return { 
          exists: budgetState.value.exists, 
          budgetId: budgetState.value.existingId 
        };
      }

      const nextMonth = getNextMonth();
      
      // Check if the budget exists for next month
      const validationResult = await validateBudgetExists(
        userId, 
        nextMonth.month, 
        nextMonth.year,
        forceRefresh
      );
      
      if (process.client) {
        // Removed debug logging;
      }
      
      // Update global state if route isn't already to the wizard with this budget
      const currentBudgetId = route?.query?.budgetId;
      
      // Set the exists and existingId properties regardless of the current route
      // This ensures consistent budget recognition across the application
      budgetState.value.exists = validationResult.exists;
      budgetState.value.existingId = validationResult.budgetId;
      
      if (process.client) {
        // Updated global state after validation
      }
      
      budgetState.value.lastUpdated = now;
      budgetState.value.lastValidationTime = now;
      return validationResult;
    };
    
    // Function to synchronize budget state specifically when navigating between pages
    const syncBudgetState = async () => {
      const route = useRoute();
      const routePath = route.path || '';
      const userId = useState<string>('selectedUser').value;
      
      // Check if userId is valid
      const isValidUserId = userId && userId !== 'testid';
      
      if (process.client) {
        // Removed debug logging;
      }
      
      // Force refresh if we're on the home page or if the userId is 'testid'
      const forceRefresh = routePath === '/' || !isValidUserId;
      
      const result = await refreshBudgetState(route, forceRefresh);
      if (process.client) {
        // Removed debug logging;
      }
      return result;
    };

    return {
      provide: {        resetBudgetState: () => {
          if (process.client) {
            // Removed debug logging;
          }
          budgetState.value = {            details: null,
            isLoading: false,
            isSaving: false,
            currentStep: 0,
            exists: false,
            existingId: null,
            initialized: false,
            lastUpdated: Date.now(),
            lastValidationTime: 0
          };
          
          // If we're on the wizard page with a budgetId, we need to trigger a reload
          if (process.client) {
            const route = useRoute();
            if (route.path === '/wizard' && route.query.budgetId) {
              // Removed debug logging;
              // We don't need to do anything here - the route watcher in wizard.vue will handle reloading
            }
          }
        },
        refreshBudgetState,
        validateBudgetExists,
        syncBudgetState
      }
    };
  }
});
